import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from 'recharts';
import { WidgetCard } from './WidgetCard';
import { MOCK_RULE_HITS } from '../mocks/ruleHits';
import type { RuleHit } from '../types';

const categoryGradients: Record<string, [string, string]> = {
  Fraud: ['#3b82f6', '#1d4ed8'],
  AML: ['#8b5cf6', '#6d28d9'],
  Sanctions: ['#ef4444', '#b91c1c'],
};

const defaultGradient: [string, string] = ['#64748b', '#475569'];

function categoryId(category: string): string {
  return `grad-${category.toLowerCase().replace(/\s+/g, '-')}`;
}

function truncateName(name: string, max = 18): string {
  return name.length > max ? `${name.slice(0, max)}…` : name;
}

export function RuleHitChartWidget() {
  return (
    <WidgetCard title="Rule Hits">
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={MOCK_RULE_HITS}
            margin={{ top: 24, right: 8, left: -12, bottom: 64 }}
          >
            <defs>
              {Object.entries(categoryGradients).map(([category, [from, to]]) => {
                const id = categoryId(category);
                return (
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={from} />
                    <stop offset="100%" stopColor={to} />
                  </linearGradient>
                );
              })}
              <linearGradient id={categoryId('default')} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={defaultGradient[0]} />
                <stop offset="100%" stopColor={defaultGradient[1]} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="ruleName"
              angle={-35}
              textAnchor="end"
              interval={0}
              height={80}
              tick={{ fill: '#4b5563', fontSize: 11 }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={false}
              tickFormatter={(value: string) => truncateName(value, 14)}
            />
            <YAxis
              tick={{ fill: '#4b5563', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: '#f3f4f6' }}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const item = payload[0].payload as RuleHit;
                return (
                  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg">
                    <div className="text-xs font-medium text-gray-500">{item.category}</div>
                    <div className="text-sm font-semibold text-gray-900">{item.ruleName}</div>
                    <div className="text-sm text-gray-700">{item.hits.toLocaleString()} hits</div>
                  </div>
                );
              }}
            />
            <Bar
              dataKey="hits"
              radius={[6, 6, 0, 0]}
              isAnimationActive
              animationDuration={1200}
              animationBegin={150}
              animationEasing="ease-out"
            >
              <LabelList
                dataKey="hits"
                position="top"
                className="fill-gray-600 text-[11px] font-medium"
              />
              {MOCK_RULE_HITS.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={`url(#${categoryId(entry.category)})`}
                  stroke={categoryGradients[entry.category]?.[1] ?? defaultGradient[1]}
                  strokeWidth={1}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-600">
        {Object.keys(categoryGradients).map((category) => (
          <div key={category} className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: `linear-gradient(135deg, ${categoryGradients[category][0]}, ${categoryGradients[category][1]})` }}
            />
            <span>{category}</span>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}
